/**
 * Format a catalog price (cents + ISO currency) for a price pill — used for
 * passes, token packs, and the pack storefront, e.g. 7900/"USD" -> "$79",
 * 399/"USD" -> "$3.99". Whole-currency-unit prices drop the ".00" cents
 * ("no cents" pricing philosophy); fractional amounts keep two decimals and
 * are NEVER rounded (a store-configured $3.99 must read "$3.99", not "$4").
 *
 * On web these are USD reference prices from the server catalog (no live
 * store prices — web has no purchases).
 */
export function formatCatalogPrice(cents: number, currency: string): string {
  const fractionDigits = cents % 100 === 0 ? 0 : 2;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(fractionDigits)}`;
  }
}

/**
 * Display price for ANY web-sellable catalog product (Facade Pack, Pro Plan,
 * Passes, token packages — storefront cards and paywalls alike): the
 * server-computed, tax-inclusive localized total (Paddle pricing-preview,
 * already formatted with the right currency symbol) when available, else the
 * static USD reference price. EVERY price surface must go through this —
 * otherwise a buyer sees two different numbers for the same product depending
 * on where they look (the exact skew this consolidates: a $99 pill over a
 * $59 checkout).
 */
export function formatPackPrice(attrs: {
  localized_price?: string | null;
  price: number;
  currency: string;
}): string {
  return attrs.localized_price ?? formatCatalogPrice(attrs.price, attrs.currency);
}
